class SmartSizingTool extends HTMLElement {
    constructor() {
        super();
        this.ruleSet = null;
        this.matchingCombos = null;
        this.suggestProducts = null;
        this.suggestProductsAbortController = null;
        this.suggestProductsRequestId = 0;
    }

    connectedCallback() {
        this.init();
    }

    disconnectedCallback() {}

    /**
     * Sanitize string for handle: replace spaces with hyphens, remove special chars, lowercase
     */
    sanitizeForHandle(value) {
        if (!value) return "";
        return value
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, "-") // Replace spaces with hyphens
            .replace(/[^a-z0-9-]/g, "") // Remove special characters except hyphens
            .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
            .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
    }

    /**
     * Generate handle for combo based on format: {inside_width}-{adjustable_bed}-{couch_type}-couch-{fabric}
     * Example: 27-adjustable-perfection-cut-couch-arbutus-velvet
     */
    generateComboHandle(combo) {
        const parts = [];

        // Inside width
        if (combo.inside_width) {
            parts.push(combo.inside_width.toString());
        }

        // Adjustable bed
        // - true  -> "adjustable"
        // - false -> "non-adjustable"
        if (combo.adjustable_bed === true || combo.adjustable_bed === "true") {
            parts.push("adjustable");
        } else if (
            combo.adjustable_bed === false ||
            combo.adjustable_bed === "false" ||
            // handle cases where metafield might be blank/null but explicitly represents "not adjustable"
            combo.adjustable_bed === 0
        ) {
            parts.push("non-adjustable");
        }

        // Couch type (sanitize spaces and special chars)
        if (combo.couch_type) {
            parts.push(this.sanitizeForHandle(combo.couch_type));
        }

        // Fabric (sanitize spaces and special chars)
        if (combo.fabric) {
            parts.push(this.sanitizeForHandle(combo.fabric));
        }

        const handle = parts.filter(Boolean).join("-");

        return handle;
    }

    init() {
        this.ruleSet = JSON.parse(
            this.querySelector('script[type="application/json"]').textContent,
        );

        // Add handle field to each combo
        if (this.ruleSet.combos && Array.isArray(this.ruleSet.combos)) {
            this.ruleSet.combos = this.ruleSet.combos.map((combo) => {
                const handle = this.generateComboHandle(combo);
                return {
                    ...combo,
                    handle,
                };
            });
        }

        this.smartSizingToolGroup = this.querySelector(".smart-sizing-tool__select-group");
        this.smartSizingToolGroupTemplate = this.querySelector(
            "template#inside-width-group-template",
        );
        this.genericInsertProductTemplate = this.querySelector(
            "template#generic-insert-product-template",
        );
        this.suggestProducts = this.querySelector(".smart-sizing-tool__suggest-products");

        // Ensure there's always a container we can replace with /search?section_id responses
        if (!this.suggestProducts) {
            const container = document.createElement("div");
            container.className = "smart-sizing-tool__suggest-products";
            container.style.marginTop = "40px";
            this.appendChild(container);
            this.suggestProducts = container;
        }

        // Snapshot initial suggest-products HTML to restore generic fallback when no match
        this.initialSuggestProductsHTML = this.suggestProducts ?
            this.suggestProducts.outerHTML :
            null;

        this.suggestProducts.style.display = "none";

        this.initModelSelectionListener();
        this.initAddToCartListener();

        console.log("this.ruleSet", this.ruleSet);
        this.render();
    }

    setMainProductQuantityVisible(isVisible) {
        const form = document.querySelector(".js-product-form-main");
        if (!form) return;

        // Hide/show the "Quantity" label
        const qtyLabel = form.querySelector(".product-info__quantity");
        if (qtyLabel) {
            qtyLabel.style.display = isVisible ? "" : "none";
        }

        // Hide/show the quantity-input component inside add-to-cart row
        const addToCartRow = form.querySelector(".product-info__add-to-cart");
        const qtyComponent = addToCartRow && addToCartRow.querySelector("quantity-input");
        if (qtyComponent) {
            qtyComponent.style.display = isVisible ? "" : "none";
        }

        // Toggle a helper class so CSS can make the ATC button full width when quantity is hidden
        if (addToCartRow) {
            addToCartRow.classList.toggle("smart-sizing--no-quantity", !isVisible);
        }
    }

    initModelSelectionListener() {
        const modelSelector = this.querySelector('[data-component-id="model-selector"]');
        if (!modelSelector) return;

        const modelOptionsContainer = modelSelector.querySelector(".dropdown-component__options");
        if (!modelOptionsContainer) return;

        // Use event delegation - only add listener once
        modelOptionsContainer.addEventListener("click", (e) => {
            // If brand is required, prevent selecting model before a brand is chosen
            const brandRequirement = this.ruleSet && this.ruleSet.brand_requirement;
            if (brandRequirement === "required") {
                const url = new URL(window.location.href);
                const brand = url.searchParams.get("brand");
                if (!brand || !brand.trim()) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.showFieldErrors(["brand"]);
                    return;
                }
            }

            const option = e.target.closest(".dropdown-component__option");
            if (option) {
                const selectedModel = option.getAttribute("data-model");
                if (selectedModel) {
                    if (selectedModel === "Other") {
                        // Show custom model input
                        const customModelInput = this.querySelector(
                            ".smart-sizing-tool__custom-model-input",
                        );
                        if (customModelInput) {
                            customModelInput.classList.remove("hidden");
                            customModelInput.querySelector("input").focus();
                        }
                    } else {
                        // Hide custom model input when regular model is selected
                        const customModelInput = this.querySelector(
                            ".smart-sizing-tool__custom-model-input",
                        );
                        if (customModelInput) {
                            customModelInput.classList.add("hidden");
                            customModelInput.querySelector("input").value = "";
                        }

                        // Remove custom model param when regular model is selected
                        this.removeParamsFromUrl(["custom_model"]);
                    }

                    // Sync model selection to URL
                    const url = new URL(window.location.href);
                    url.searchParams.set("model", selectedModel);
                    // Reset all option params when switching model
                    ["inside_widths", "adjustable_beds", "couch_types", "fabrics"].forEach(
                        (key) => {
                            url.searchParams.delete(key);
                        },
                    );
                    window.history.replaceState({}, "", url.toString());

                    // Reset UI: remove active class from all option buttons
                    this.resetOptionButtons();

                    // Clear field errors when model is selected
                    this.clearFieldErrors();

                    // Recompute visibility & availability after model change
                    this.updateGroupOptionsVisibility();
                    this.updateOptionAvailability();
                    this.updateSuggestProductsVisibility();
                }
            }
        });

        if (this.matchingCombos && this.matchingCombos.length > 0) {
            const genericInsertProductTemplate =
                this.genericInsertProductTemplate.content.cloneNode(true);
            const genericInsertProduct = genericInsertProductTemplate.querySelector(
                ".smart-sizing-tool__select-group-item",
            );
            genericInsertProduct.setAttribute("data-key", "generic-insert-product");
            genericInsertProduct.querySelector(
                ".smart-sizing-tool__select-group-item-label",
            ).textContent = this.matchingCombos[0].product.title;
            this.smartSizingToolGroup.appendChild(genericInsertProduct);
        }
    }

    render() {
        this.renderBrands();
        // Update brand visibility after brands are rendered (needs this.ruleSet.brands)
        this.updateBrandVisibility();
        this.renderGroups();
        this.initUrlParams();
        // Update model visibility after models might be rendered
        this.updateModelVisibility();

        // Ensure model selector enabled/disabled correctly based on brand selection
        this.updateModelEnabledState();

        // Initial check based on any URL params present on load
        this.updateGroupOptionsVisibility();
        this.updateOptionAvailability();
        this.updateSuggestProductsVisibility();
    }

    /**
     * Show/hide group options (inside_widths, adjustable_beds, etc.)
     */
    updateGroupOptionsVisibility() {
        if (!this.ruleSet) return;

        const url = new URL(window.location.href);
        const params = url.searchParams;

        // Check brand requirement
        const brandRequirement = this.ruleSet.brand_requirement;
        const brand = params.get("brand");
        const brandIsRequired = brandRequirement === "required";
        const hasBrand = !!params.get("brand");

        // Check model requirement
        const modelRequirement = this.ruleSet.model_requirement;
        const modelIsRequired = modelRequirement === "required";
        const modelIsHidden = modelRequirement === "hidden";
        const hasModel = !!params.get("model");

        // Show groups if: (brand is optional OR brand is provided) AND (model is hidden OR model is optional OR model is provided)
        const shouldShowGroups =
            (!brandIsRequired || hasBrand) && (modelIsHidden || !modelIsRequired || hasModel);

        const groupKeys = ["inside_widths", "adjustable_beds", "couch_types", "fabrics"];
        groupKeys.forEach((key) => {
            const groupItem = this.querySelector(
                `.smart-sizing-tool__select-group-item[data-key="${key.toLowerCase()}"]`,
            );
            if (groupItem) {
                groupItem.style.display = shouldShowGroups ? "" : "none";
            }
        });
    }

    /**
     * Show/hide the suggested products panel depending on whether
     * all required options have been selected.
     */
    updateSuggestProductsVisibility() {
        if (!this.suggestProducts || !this.ruleSet) return;

        this.#applyHiddenAdjustableBedDefault();

        const url = new URL(window.location.href);
        const params = url.searchParams;

        // Intro message shown until required brand/model (if any) are selected
        const introMessage = this.querySelector(".smart-sizing-tool__notification-message");
        const otherMessage = this.querySelector(".smart-sizing-tool__notification-message--Other");

        // Check brand requirement
        const brandRequirement = this.ruleSet.brand_requirement;
        const brandIsRequired = brandRequirement === "required";
        const brand = params.get("brand");
        const hasBrand = !!brand;

        // Check model requirement
        const modelRequirement = this.ruleSet.model_requirement;
        const modelIsRequired = modelRequirement === "required";
        const modelIsHidden = modelRequirement === "hidden";
        const model = params.get("model");
        const hasModel = !!model; // treat "Other" as a valid selected model

        const isOtherBoth = brand === "Other" && model === "Other";
        const missingRequiredBrandOrModel =
            (brandIsRequired && !hasBrand) || (!modelIsHidden && modelIsRequired && !hasModel);

        // Default hide both helper messages
        if (introMessage) introMessage.style.display = "none";
        if (otherMessage) otherMessage.style.display = "none";

        // Hide suggested products if required fields are missing (skip model check if hidden)
        if (missingRequiredBrandOrModel) {
            // Show intro helper when brand/model are still missing
            if (introMessage) introMessage.style.display = "";
            this.suggestProducts.style.display = "none";
            this.setMainProductQuantityVisible(true);
            return;
        }

        // These are the option groups we render buttons for
        const groupKeys = ["inside_widths", "adjustable_beds", "couch_types", "fabrics"];
        const hideAdjustableBed =
            this.ruleSet.hide_adjustable_bed_if_one_value === true ||
            this.ruleSet.hide_adjustable_bed_if_one_value === "true";

        let allGroupsSelected = true;
        for (const key of groupKeys) {
            if (key === "adjustable_beds" && hideAdjustableBed) continue;

            // Only enforce groups that actually exist in this rule set and have values
            const groupConfig = this.ruleSet[key];
            if (groupConfig && Array.isArray(groupConfig.values) && groupConfig.values.length > 0) {
                if (!params.get(key)) {
                    allGroupsSelected = false;
                    break;
                }
            }
        }

        // When both brand and model are "Other" and all options selected, show the "You're all set" message
        if (otherMessage) {
            otherMessage.style.display = isOtherBoth && allGroupsSelected ? "" : "none";
        }

        this.suggestProducts.style.display = allGroupsSelected ? "" : "none";
        this.setMainProductQuantityVisible(!allGroupsSelected);

        // Update product content when all selections are made (including when brand/model is Other — match by options; custom inputs only used at ATC)
        if (allGroupsSelected) {
            this.updateSuggestProducts();
        }
    }

    /**
     * After any filter change (brand/model/option), recompute which option values
     * are still valid based on remaining combos and disable impossible ones.
     */
    updateOptionAvailability() {
        if (!this.ruleSet || !this.ruleSet.combos) return;

        const groupKeys = ["inside_widths", "adjustable_beds", "couch_types", "fabrics"];
        groupKeys.forEach((key) => this.updateSingleGroupAvailability(key));
    }

    updateSingleGroupAvailability(groupKey) {
        // Find the group container for this key
        const groupItem = this.querySelector(
            `.smart-sizing-tool__select-group-item[data-key="${groupKey.toLowerCase()}"]`,
        );
        if (!groupItem) return;

        const buttons = groupItem.querySelectorAll(".smart-sizing-tool__select-group-item-button");
        if (!buttons.length) return;

        // Determine which values are still valid for this group, given all Other selections
        const validValues = this.getValidValuesForGroup(groupKey);

        // If we can't determine any valid values (e.g. no combos), don't disable anything
        if (!validValues) {
            buttons.forEach((button) => {
                button.classList.remove("is-disabled");
                button.disabled = false;
            });
            return;
        }

        // If the currently selected value for this group becomes invalid, also clear it from URL
        const url = new URL(window.location.href);
        const currentValue = url.searchParams.get(groupKey);
        const shouldClearCurrent = currentValue && !validValues.has(currentValue);
        if (shouldClearCurrent) {
            url.searchParams.delete(groupKey);
            window.history.replaceState({}, "", url.toString());
        }

        buttons.forEach((button) => {
            const value = button.getAttribute("data-value");
            if (!value) return;

            const isValid = validValues.has(value);

            if (isValid) {
                button.classList.remove("is-disabled");
                button.disabled = false;
            } else {
                button.classList.add("is-disabled");
                button.disabled = true;
                button.classList.remove("active");
            }
        });
    }

    /**
     * Compute the set of valid values for a given groupKey, based on current URL selections.
     * Returns a Set of URL values (string), or null if we should not constrain this group.
     */
    getValidValuesForGroup(groupKey) {
        if (!this.ruleSet || !this.ruleSet.combos) return null;

        const url = new URL(window.location.href);
        const params = url.searchParams;

        const brand = params.get("brand");
        const model = params.get("model");
        const insideWidth = params.get("inside_widths");
        const adjustableBed = params.get("adjustable_beds");
        const couchType = params.get("couch_types");
        const fabric = params.get("fabrics");

        const isOtherSelection = brand === "Other" || model === "Other";

        // Filter combos by current selections, *excluding* the group we're computing for
        const filtered = this.ruleSet.combos.filter((combo) => {
            if (!combo.active) return false;

            // Brand / model matching (when brand/model are used)
            if (isOtherSelection) {
                // When brand or model is "Other", only consider combos that actually have
                // an "Other" model attached in their models array.
                const hasOtherModel =
                    Array.isArray(combo.models) &&
                    combo.models.some(
                        (m) =>
                        m &&
                        String(m.model_name || "")
                        .trim()
                        .toLowerCase() === "other",
                    );
                if (!hasOtherModel) return false;
            } else if (brand) {
                const matchesBrand = combo.models.some((m) => m.brand === brand);
                if (!matchesBrand) return false;

                if (model && this.ruleSet.model_requirement !== "hidden") {
                    const matchesModel = combo.models.some(
                        (m) => m.brand === brand && m.model_name === model,
                    );
                    if (!matchesModel) return false;
                }
            }

            // Inside width
            if (groupKey !== "inside_widths" && insideWidth && combo.inside_width !== insideWidth) {
                return false;
            }

            // Adjustable bed (stored as boolean/bare value, compared as string)
            if (
                groupKey !== "adjustable_beds" &&
                adjustableBed !== null &&
                adjustableBed !== "" &&
                String(combo.adjustable_bed) !== adjustableBed
            ) {
                return false;
            }

            // Couch type (compare using handleized value)
            if (
                groupKey !== "couch_types" &&
                couchType &&
                this.sanitizeForHandle(combo.couch_type) !== couchType
            ) {
                return false;
            }

            // Fabric (compare using handleized value)
            if (
                groupKey !== "fabrics" &&
                fabric &&
                this.sanitizeForHandle(combo.fabric) !== fabric
            ) {
                return false;
            }

            return true;
        });

        // If nothing to base constraints on, don't disable anything
        if (!filtered.length) return null;

        const values = new Set();

        filtered.forEach((combo) => {
            switch (groupKey) {
                case "inside_widths":
                    if (combo.inside_width != null) {
                        values.add(String(combo.inside_width));
                    }
                    break;
                case "adjustable_beds":
                    if (combo.adjustable_bed != null) {
                        values.add(String(combo.adjustable_bed));
                    }
                    break;
                case "couch_types":
                    if (combo.couch_type) {
                        values.add(this.sanitizeForHandle(combo.couch_type));
                    }
                    break;
                case "fabrics":
                    if (combo.fabric) {
                        values.add(this.sanitizeForHandle(combo.fabric));
                    }
                    break;
                default:
                    break;
            }
        });

        return values;
    }

    /**
     * Find matching combo based on current selections
     */
    findMatchingCombo() {
        if (!this.ruleSet || !this.ruleSet.combos) return null;

        const url = new URL(window.location.href);
        const params = url.searchParams;

        const brand = params.get("brand");
        const model = params.get("model");
        const insideWidth = params.get("inside_widths");
        const adjustableBed = params.get("adjustable_beds");
        const couchType = params.get("couch_types");
        const fabric = params.get("fabrics");

        console.log("[Smart Sizing Tool] Finding matching combo with options:", {
            brand,
            model,
            inside_widths: insideWidth,
            adjustable_beds: adjustableBed,
            couch_types: couchType,
            fabrics: fabric,
        });

        // When brand or model is "Other", match by options only — same flow as other selections; custom text is only for ATC properties
        if (brand === "Other" || model === "Other") {
            const matchByOptionsOnly = this.ruleSet.combos.find((combo) => {
                if (!combo.active) return false;
                if (insideWidth && combo.inside_width !== insideWidth) return false;
                if (
                    adjustableBed !== null &&
                    adjustableBed !== "" &&
                    String(combo.adjustable_bed) !== adjustableBed
                )
                    return false;
                if (couchType && this.sanitizeForHandle(combo.couch_type) !== couchType)
                    return false;
                if (fabric && this.sanitizeForHandle(combo.fabric) !== fabric) return false;
                return true;
            });
            if (matchByOptionsOnly) {
                console.log(
                    "[Smart Sizing Tool] Brand/model is Other, matched combo by options only:", {
                        handle: matchByOptionsOnly.handle,
                        product: matchByOptionsOnly.product,
                    },
                );
                return matchByOptionsOnly;
            }
            console.log(
                "[Smart Sizing Tool] Brand/model is Other but no combo matches options, using generic",
            );
            return null;
        }

        // Check model requirement
        const modelRequirement = this.ruleSet.model_requirement;
        const modelIsHidden = modelRequirement === "hidden";
        const modelIsOptional = modelRequirement === "optional";

        // If brand is optional and not provided, cannot match combo (will use generic)
        const brandRequirement = this.ruleSet.brand_requirement;
        const brandIsOptional = brandRequirement === "optional";
        if (brandIsOptional && !brand) {
            console.log("[Smart Sizing Tool] Brand is optional and not provided, skipping match");
            return null;
        }

        // If model is optional and not provided, cannot match combo (will use generic)
        if (modelIsOptional && !model) {
            console.log("[Smart Sizing Tool] Model is optional and not provided, skipping match");
            return null;
        }

        // Find matching combo
        const matchingCombo = this.ruleSet.combos.find((combo) => {
            // Check if combo is active
            if (!combo.active) return false;

            // Check brand and model match
            if (brand) {
                // If model is hidden, only match by brand
                if (modelIsHidden) {
                    const brandMatch = combo.models.some((m) => m.brand === brand);
                    if (!brandMatch) return false;
                } else if (model) {
                    // Match by both brand and model
                    const brandModelMatch = combo.models.some(
                        (m) => m.brand === brand && m.model_name === model,
                    );
                    if (!brandModelMatch) return false;
                } else {
                    // Brand provided but model not provided and not hidden/optional
                    return false;
                }
            } else {
                // If brand is optional and not provided, cannot match (should have been caught above)
                return false;
            }

            // Check option matches
            if (insideWidth && combo.inside_width !== insideWidth) return false;
            if (adjustableBed !== null && combo.adjustable_bed.toString() !== adjustableBed)
                return false;
            if (couchType && this.sanitizeForHandle(combo.couch_type) !== couchType) return false;
            if (fabric && this.sanitizeForHandle(combo.fabric) !== fabric) return false;

            return true;
        });

        if (matchingCombo) {
            console.log("[Smart Sizing Tool] Found matching combo:", {
                handle: matchingCombo.handle,
                combo: {
                    inside_width: matchingCombo.inside_width,
                    adjustable_bed: matchingCombo.adjustable_bed,
                    couch_type: matchingCombo.couch_type,
                    fabric: matchingCombo.fabric,
                },
                product: matchingCombo.product,
            });
        } else {
            console.log("[Smart Sizing Tool] No matching combo found, will use generic product");
        }

        return matchingCombo || null;
    }

    /**
     * Update suggest products with matching product or generic product
     * Fetches product from metaobject via /search?section_id=get-metaobject&q=handle
     */
    async updateSuggestProducts() {
        if (!this.suggestProducts || !this.ruleSet) return;

        const matchingCombo = this.findMatchingCombo();
        const productHandle = matchingCombo && matchingCombo.handle;

        if (!productHandle) {
            // No match found, fetch generic product section and replace
            console.log("[Smart Sizing Tool] No match found, fetching generic product");
            await this.restoreGenericProduct();
            return;
        }

        console.log("[Smart Sizing Tool] Using matching combo handle:", productHandle);
        const ok = await this.fetchSuggestProductsSectionByHandle(productHandle);
        if (!ok) await this.restoreGenericProduct();
    }

    /**
     * Update product display with product data
     */
    updateProductDisplay(product) {
        if (!this.suggestProducts || !product) return;

        // Update product title
        const titleElement = this.suggestProducts.querySelector(
            ".smart-sizing-tool__suggest-products-item > div:last-child > div:first-child",
        );
        if (titleElement) {
            titleElement.textContent = product.title;
        }

        // Update product image
        const imageContainer = this.suggestProducts.querySelector(
            ".smart-sizing-tool__suggest-products-item > div:first-child",
        );
        if (imageContainer && product.featured_image) {
            imageContainer.innerHTML = `<img src="${product.featured_image}" alt="${product.title}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 10px;" />`;
        }

        // Update prices
        const priceContainer = this.suggestProducts.querySelector(
            ".smart-sizing-tool__suggest-products-item > div:last-child > div:last-child",
        );
        if (priceContainer) {
            const priceSpans = priceContainer.querySelectorAll('span[style*="font-weight:600"]');
            if (priceSpans.length >= 2) {
                // Format price using Shopify's money format
                const formatMoney = (cents) => {
                    return new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                    }).format(cents / 100);
                };

                priceSpans[0].textContent = formatMoney(product.price);
                priceSpans[1].textContent = formatMoney(product.price * 2);
            }
        }

        // Update add to cart button variant ID if exists
        const addToCartBtn = this.suggestProducts.querySelector(
            ".smart-sizing-tool__add-to-cart-btn",
        );
        if (addToCartBtn && product.variants && product.variants.length > 0) {
            addToCartBtn.dataset.variantId = product.variants[0].id;
        }
    }

    /**
     * Show generic product (fallback)
     */
    showGenericProduct() {
        // Generic product is already rendered in liquid, just ensure it's visible
        if (this.suggestProducts) {
            this.suggestProducts.style.display = "";
        }
    }

    hideSuggestProducts() {
        if (this.suggestProducts) {
            this.suggestProducts.style.display = "none";
        }
    }

    setSuggestProductsLoading(isLoading) {
        if (!this.suggestProducts) return;

        if (isLoading) {
            this.suggestProducts.style.display = "";
            this.suggestProducts.classList.add("is-loading");
            this.suggestProducts.setAttribute("aria-busy", "true");

            let loader = this.suggestProducts.querySelector(
                ".smart-sizing-tool__suggest-products-loading",
            );
            if (!loader) {
                loader = document.createElement("div");
                loader.className = "smart-sizing-tool__suggest-products-loading";
                loader.setAttribute("role", "status");
                loader.setAttribute("aria-live", "polite");

                const spinner = document.createElement("span");
                spinner.className = "smart-sizing-tool__spinner";
                spinner.setAttribute("aria-hidden", "true");

                const text = document.createElement("span");
                text.textContent = "Loading";

                loader.appendChild(spinner);
                loader.appendChild(text);

                this.suggestProducts.prepend(loader);
            }

            return;
        }

        this.suggestProducts.classList.remove("is-loading");
        this.suggestProducts.removeAttribute("aria-busy");
        this.suggestProducts.querySelector(".smart-sizing-tool__suggest-products-loading") &&
            this.suggestProducts
            .querySelector(".smart-sizing-tool__suggest-products-loading")
            .remove();
    }

    async fetchSuggestProductsSectionByHandle(handle) {
        if (!handle) return false;

        // Prevent race conditions: abort any in-flight request and only apply the latest response
        if (this.suggestProductsAbortController) {
            this.suggestProductsAbortController.abort();
        }
        const controller = new AbortController();
        this.suggestProductsAbortController = controller;
        const requestId = ++this.suggestProductsRequestId;

        try {
            this.setSuggestProductsLoading(true);

            const url = `/search?section_id=get-metaobject&q=${encodeURIComponent(handle)}`;
            console.log("[Smart Sizing Tool] Fetching suggest products section:", url);

            const response = await fetch(url, {
                signal: controller.signal,
            });
            if (!response.ok) {
                if (requestId === this.suggestProductsRequestId)
                    this.setSuggestProductsLoading(false);
                return false;
            }

            const html = await response.text();

            // If a newer request started while we were waiting, ignore this response.
            if (requestId !== this.suggestProductsRequestId) return false;

            const parsed = new DOMParser().parseFromString(html, "text/html");
            const nextSuggestProducts = parsed.querySelector(
                ".smart-sizing-tool__suggest-products",
            );
            if (!nextSuggestProducts) {
                if (requestId === this.suggestProductsRequestId)
                    this.setSuggestProductsLoading(false);
                return false;
            }

            if (this.suggestProducts) {
                this.suggestProducts.replaceWith(nextSuggestProducts);
            }
            this.suggestProducts = this.querySelector(".smart-sizing-tool__suggest-products");
            if (this.suggestProducts) {
                this.suggestProducts.style.display = "";
                // Verify variant ID is present after replacement
                const variantId =
                    this.suggestProducts.dataset.variantId ||
                    this.suggestProducts.getAttribute("data-variant-id");
                if (!variantId) {
                    console.warn(
                        "[Smart Sizing Tool] Warning: No variant-id attribute found on replaced suggestProducts element",
                    );
                }
            }
            if (requestId === this.suggestProductsRequestId) this.setSuggestProductsLoading(false);

            return true;
        } catch (error) {
            if (error && error.name === "AbortError") return false;
            console.error("[Smart Sizing Tool] Error fetching suggest products section:", error);
            if (requestId === this.suggestProductsRequestId) this.setSuggestProductsLoading(false);
            return false;
        }
    }

    async restoreGenericProduct() {
        const genericHandle =
            this.ruleSet &&
            this.ruleSet.generic_insert_product &&
            this.ruleSet.generic_insert_product.handle;
        if (!genericHandle) {
            this.hideSuggestProducts();
            this.setMainProductQuantityVisible(true);
            return;
        }

        const ok = await this.fetchSuggestProductsSectionByHandle(genericHandle);
        if (ok) return;

        // Last resort: restore snapshot if available, otherwise hide
        if (this.initialSuggestProductsHTML && this.suggestProducts) {
            const parsed = new DOMParser().parseFromString(
                this.initialSuggestProductsHTML,
                "text/html",
            );
            const generic = parsed.querySelector(".smart-sizing-tool__suggest-products");
            if (generic) {
                this.suggestProducts.replaceWith(generic);
                this.suggestProducts = generic;
                this.suggestProducts.style.display = "";
                this.setMainProductQuantityVisible(false);
                return;
            }
        }

        this.hideSuggestProducts();
        this.setMainProductQuantityVisible(true);
    }

    /**
     * Initialize UI state from URL parameters:
     * - brand (and related custom brand)
     * - model (and related custom model)
     * - option group button selections
     */
    initUrlParams() {
        const urlParams = new URLSearchParams(window.location.search);

        this.initBrandFromUrl(urlParams);

        // Update group options visibility based on brand selection
        this.updateGroupOptionsVisibility();

        this.initModelFromUrl(urlParams);
        this.initOptionButtonsFromUrl(urlParams);

        this.#applyHiddenAdjustableBedDefault();
    }

    /**
     * When Adjustable Bed group is hidden (hide_adjustable_bed_if_one_value), set default
     * adjustable_beds in URL from the single value so combo matching still works.
     */
    #applyHiddenAdjustableBedDefault() {
        if (!this.ruleSet) return;

        const hide =
            this.ruleSet.hide_adjustable_bed_if_one_value === true ||
            this.ruleSet.hide_adjustable_bed_if_one_value === "true";
        if (!hide) return;

        const groupConfig = this.ruleSet.adjustable_beds;
        if (!groupConfig || !Array.isArray(groupConfig.values) || groupConfig.values.length === 0)
            return;

        const uniqueValues = [...new Set(groupConfig.values)];
        if (uniqueValues.length !== 1) return;

        const url = new URL(window.location.href);
        if (url.searchParams.get("adjustable_beds")) return;

        const singleValue = uniqueValues[0];
        const urlValue = singleValue != null ? String(singleValue) : "";
        url.searchParams.set("adjustable_beds", urlValue);
        window.history.replaceState({}, "", url.toString());
    }

    /**
     * Initialize brand-related UI from URL params.
     */
    initBrandFromUrl(urlParams) {
        const brandFromUrl = urlParams.get("brand");

        if (brandFromUrl === "Other") {
            // Handle "Other" brand selection
            const brandSelector = this.querySelector('[data-component-id="brand-selector"]');
            const brandHeading = brandSelector.querySelector(".dropdown-component__heading");
            const brandHeadingTextNode = brandHeading.childNodes[0];
            if (brandHeadingTextNode.nodeType === Node.TEXT_NODE) {
                brandHeadingTextNode.nodeValue = "Other ";
            }
            brandHeading.style.color = "#231f20";

            // Show custom brand input
            const customBrandInput = this.querySelector(".smart-sizing-tool__custom-brand-input");
            if (customBrandInput) {
                customBrandInput.classList.remove("hidden");
                const customBrandFromUrl = urlParams.get("custom_brand");
                if (customBrandFromUrl) {
                    customBrandInput.querySelector("input").value = customBrandFromUrl;
                }
            }

            // For brand "Other", still render all models
            this.renderModels(null);

            // Update model visibility (will handle hidden state)
            this.updateModelVisibility();

            // Update model enabled/disabled state
            this.updateModelEnabledState();

            // Keep custom model input hidden until model "Other" is chosen
            const customModelInput = this.querySelector(".smart-sizing-tool__custom-model-input");
            if (customModelInput) {
                customModelInput.classList.add("hidden");
                customModelInput.querySelector("input").value = "";
            }
            return;
        }

        if (brandFromUrl && this.ruleSet._brand[brandFromUrl]) {
            // Check if brand is hidden - if so, render all models, otherwise filter by brand
            const brandVisibility = this.ruleSet.brand_visibility;
            const isBrandHidden = brandVisibility === "hide";
            this.renderModels(isBrandHidden ? null : brandFromUrl);

            const brandSelector = this.querySelector('[data-component-id="brand-selector"]');
            const brandHeading = brandSelector.querySelector(".dropdown-component__heading");
            const brandHeadingTextNode = brandHeading.childNodes[0];
            if (brandHeadingTextNode.nodeType === Node.TEXT_NODE) {
                brandHeadingTextNode.nodeValue = brandFromUrl + " ";
            }
            brandHeading.style.color = "#231f20";

            // Update model visibility (will handle hidden state)
            this.updateModelVisibility();
            // Update model enabled/disabled state
            this.updateModelEnabledState();
            return;
        }

        // No brand in URL - check if brand is hidden, if so render all models
        const brandVisibility = this.ruleSet.brand_visibility;
        const isBrandHidden = brandVisibility === "hide";
        if (isBrandHidden) {
            this.renderModels(null);
            this.updateModelVisibility();
            this.updateModelEnabledState();
        }
    }

    /**
     * Initialize model-related UI from URL params.
     * Respects model_requirement (skips when hidden).
     */
    initModelFromUrl(urlParams) {
        // Handle model from URL (only if model is not hidden)
        const modelRequirement = this.ruleSet.model_requirement;
        const modelIsHidden = modelRequirement === "hidden";
        const modelFromUrl = urlParams.get("model");
        if (!modelFromUrl || modelIsHidden) return;

        const modelSelector = this.querySelector('[data-component-id="model-selector"]');
        if (!modelSelector) return;

        const modelHeading = modelSelector.querySelector(".dropdown-component__heading");
        const modelHeadingTextNode = modelHeading.childNodes[0];
        if (modelHeadingTextNode.nodeType === Node.TEXT_NODE) {
            modelHeadingTextNode.nodeValue =
                (modelFromUrl === "Other" ? "Other" : modelFromUrl) + " ";
        }
        modelHeading.style.color = "#231f20";

        // Show custom model input if model is "Other"
        if (modelFromUrl === "Other") {
            const customModelInput = this.querySelector(".smart-sizing-tool__custom-model-input");
            if (customModelInput) {
                customModelInput.classList.remove("hidden");
                const customModelFromUrl = urlParams.get("custom_model");
                if (customModelFromUrl) {
                    customModelInput.querySelector("input").value = customModelFromUrl;
                }
            }
        }
    }

    /**
     * Initialize option group buttons (inside_widths, adjustable_beds, etc.)
     * from URL params.
     */
    initOptionButtonsFromUrl(urlParams) {
        for (const [key, value] of urlParams.entries()) {
            if (
                key === "brand" ||
                key === "model" ||
                key === "custom_brand" ||
                key === "custom_model"
            )
                continue;

            if (!key) continue;

            // Convert key to lowercase to match data-key attribute
            const normalizedKey = key.toLowerCase();
            const groupItem = this.querySelector(
                `.smart-sizing-tool__select-group-item[data-key="${normalizedKey}"]`,
            );
            if (!groupItem) continue;

            const buttons = groupItem.querySelectorAll(
                ".smart-sizing-tool__select-group-item-button",
            );
            buttons.forEach((btn) => {
                const buttonValue = btn.getAttribute("data-value");
                if (!buttonValue) return;

                // Compare using data-value attribute for exact match
                if (buttonValue === value) {
                    btn.classList.add("active");
                } else {
                    btn.classList.remove("active");
                }
            });
        }
    }

    renderGroups() {
        const _ruleSet = this.ruleSet;

        _ruleSet.inside_widths = {
            name: "Inside Width",
            values: _ruleSet.combos.map((combo) => combo.inside_width),
            unit: "inches",
        };

        _ruleSet.adjustable_beds = {
            name: "Adjustable Bed",
            values: _ruleSet.combos.map((combo) => combo.adjustable_bed),
        };

        _ruleSet.couch_types = {
            name: "Couch Type",
            values: _ruleSet.combos.map((combo) => combo.couch_type),
        };

        _ruleSet.fabrics = {
            name: "Fabric",
            values: _ruleSet.combos.map((combo) => combo.fabric),
        };

        _ruleSet.models = {
            name: "Model",
            values: _ruleSet.combos.map((combo) => combo.models),
        };

        for (const key in _ruleSet) {
            if (["inside_widths", "adjustable_beds", "couch_types", "fabrics"].includes(key)) {
                if (
                    key === "adjustable_beds" &&
                    (this.ruleSet.hide_adjustable_bed_if_one_value === true ||
                        this.ruleSet.hide_adjustable_bed_if_one_value === "true")
                ) {
                    continue;
                }

                const group = this.smartSizingToolGroupTemplate.content.cloneNode(true);

                const groupItem = group.querySelector(".smart-sizing-tool__select-group-item");
                groupItem.setAttribute("data-key", key.toLowerCase());

                groupItem.querySelector(".smart-sizing-tool__select-group-item-label").textContent =
                    _ruleSet[key].name;

                const buttonsContainer = groupItem.querySelector(
                    ".smart-sizing-tool__select-group-item-buttons",
                );

                buttonsContainer.innerHTML = "";

                // Remove duplicates - get unique values only
                const uniqueValues = [...new Set(_ruleSet[key].values)];

                uniqueValues.forEach((value) => {
                    // Preserve a readable label, but store a URL-safe value for matching & deep links
                    let displayValue = value;

                    // Convert boolean values to "Yes"/"No" for display
                    if (value === true || value === "true") {
                        displayValue = "Yes";
                    } else if (value === false || value === "false") {
                        displayValue = "No";
                    } else if (_ruleSet[key].unit) {
                        displayValue = value + " " + _ruleSet[key].unit;
                    }

                    const urlValue =
                        key === "couch_types" || key === "fabrics" ?
                        this.sanitizeForHandle(value) :
                        value && value.toString();

                    const button = document.createElement("button");
                    button.setAttribute("data-value", urlValue);
                    button.textContent = displayValue;
                    button.classList.add("smart-sizing-tool__select-group-item-button");

                    // Add click event: set active state, update the url param
                    button.addEventListener("click", (e) => {
                        e.preventDefault();

                        // If button is already active, do nothing (prevent re-selecting same option)
                        if (button.classList.contains("active")) {
                            return;
                        }

                        // Remove active class from all buttons in group
                        buttonsContainer
                            .querySelectorAll(".smart-sizing-tool__select-group-item-button.active")
                            .forEach((btn) => btn.classList.remove("active"));

                        // Add active class to clicked button
                        button.classList.add("active");

                        // Sync selection to URL
                        const url = new URL(window.location.href);
                        url.searchParams.set(key, urlValue);
                        window.history.replaceState({}, "", url.toString());

                        // Clear field errors when option is selected
                        this.clearFieldErrors();

                        // Recompute which options are still valid after this selection
                        this.updateOptionAvailability();

                        this.updateSuggestProductsVisibility();
                    });

                    buttonsContainer.appendChild(button);
                });

                this.smartSizingToolGroup.appendChild(group);
            }
        }
    }

    renderGenericInsertProduct() {
        if (this.matchingCombos && this.matchingCombos.length > 0) {
            const templateContent = this.genericInsertProductTemplate.content.cloneNode(true);
            const genericInsertProduct = templateContent.querySelector(
                ".smart-sizing-tool__select-group-item",
            );
            genericInsertProduct.setAttribute("data-key", "generic-insert-product");
            genericInsertProduct.querySelector(
                ".smart-sizing-tool__select-group-item-label",
            ).textContent = this.matchingCombos[0].product.title;
            this.smartSizingToolGroup.appendChild(genericInsertProduct);
        }
    }

    updateBrandVisibility() {
        if (!this.ruleSet) return;

        const brandVisibility = this.ruleSet.brand_visibility;
        const brandRequirement = this.ruleSet.brand_requirement;
        const brandSelector = this.querySelector('[data-component-id="brand-selector"]');
        const brandLabel = this.querySelector(".smart-sizing-tool__label");

        // Update label to show required/optional based on brand_requirement
        if (brandLabel && brandLabel.textContent.includes("Brand")) {
            const requiredSpan = brandLabel.querySelector(".smart-sizing-tool__label-required");
            let optionalSpan = brandLabel.querySelector(".smart-sizing-tool__label-optional");

            if (brandRequirement === "optional") {
                // Hide required indicator, show optional
                if (requiredSpan) requiredSpan.style.display = "none";
                if (!optionalSpan) {
                    optionalSpan = document.createElement("span");
                    optionalSpan.className = "smart-sizing-tool__label-optional";
                    optionalSpan.textContent = "(Optional)";
                    brandLabel.appendChild(optionalSpan);
                } else {
                    optionalSpan.style.display = "";
                }
            } else {
                // Show required indicator, hide optional
                if (requiredSpan) requiredSpan.style.display = "";
                if (optionalSpan) optionalSpan.style.display = "none";
            }
        }

        // Hide brand selector and label if brand_visibility is "hide"
        if (brandVisibility === "hide") {
            if (brandSelector) {
                const brandSelectorWrapper =
                    brandSelector.closest('div[style*="position: relative"]') ||
                    brandSelector.parentElement;
                if (brandSelectorWrapper) {
                    brandSelectorWrapper.style.display = "none";
                } else {
                    brandSelector.style.display = "none";
                }
            }
            if (brandLabel && brandLabel.textContent.includes("Brand")) {
                brandLabel.style.display = "none";
            }

            // Auto-set brand in URL if not already set.
            // Prefer "Starmark" as the default when available so matching works correctly
            // even though the brand selector is hidden.
            const url = new URL(window.location.href);
            const currentBrand = url.searchParams.get("brand");
            if (!currentBrand) {
                let defaultBrand = null;

                if (this.ruleSet.brands && this.ruleSet.brands.length > 0) {
                    // Try to find a brand named "Starmark" (case-insensitive)
                    const starmarkBrand = this.ruleSet.brands.find(
                        (b) => String(b).toLowerCase() === "starmark",
                    );
                    if (starmarkBrand) {
                        defaultBrand = starmarkBrand;
                    } else if (this.ruleSet.brands.length === 1) {
                        // Fallback: if only one brand exists, use it
                        defaultBrand = this.ruleSet.brands[0];
                    }
                }

                if (defaultBrand) {
                    url.searchParams.set("brand", defaultBrand);
                    window.history.replaceState({}, "", url.toString());
                }
            }

            // When brand is hidden, render all models (not filtered by brand)
            this.renderModels(null);
            // Brand is effectively chosen, ensure model is enabled
            this.updateModelEnabledState();
        } else {
            // Show brand selector and label if not hidden
            if (brandSelector) {
                const brandSelectorWrapper =
                    brandSelector.closest('div[style*="position: relative"]') ||
                    brandSelector.parentElement;
                if (brandSelectorWrapper) {
                    brandSelectorWrapper.style.display = "";
                } else {
                    brandSelector.style.display = "";
                }
            }
            if (brandLabel && brandLabel.textContent.includes("Brand")) {
                brandLabel.style.display = "";
            }
        }
    }

    updateModelVisibility() {
        if (!this.ruleSet) return;

        const modelRequirement = this.ruleSet.model_requirement;
        const modelSelectorWrapper = this.querySelector(".smart-sizing-tool__model-selector");
        const modelLabel = modelSelectorWrapper ?
            modelSelectorWrapper.querySelector(".smart-sizing-tool__label") :
            null;

        // Hide model selector and label if model_requirement is "hidden"
        if (modelRequirement === "hidden") {
            if (modelSelectorWrapper) {
                modelSelectorWrapper.style.display = "none";
                modelSelectorWrapper.classList.add("hidden");
            }
            if (modelLabel) {
                modelLabel.style.display = "none";
            }

            // Auto-set model in URL if not already set and only one model exists for selected brand
            const url = new URL(window.location.href);
            const currentModel = url.searchParams.get("model");
            const brand = url.searchParams.get("brand");
            if (!currentModel && brand && this.ruleSet._brand && this.ruleSet._brand[brand]) {
                const models = this.ruleSet._brand[brand].models;
                if (models && models.length === 1) {
                    url.searchParams.set("model", models[0]);
                    window.history.replaceState({}, "", url.toString());
                }
            }
        } else {
            // Show model selector and label if not hidden
            if (modelSelectorWrapper) {
                modelSelectorWrapper.style.display = "";
                modelSelectorWrapper.classList.remove("hidden");
            }
            if (modelLabel) {
                modelLabel.style.display = "";
            }

            // Update label to show required/optional based on model_requirement
            if (modelLabel && modelLabel.textContent.includes("Model")) {
                const requiredSpan = modelLabel.querySelector(".smart-sizing-tool__label-required");
                let optionalSpan = modelLabel.querySelector(".smart-sizing-tool__label-optional");

                if (modelRequirement === "optional") {
                    // Hide required indicator, show optional
                    if (requiredSpan) requiredSpan.style.display = "none";
                    if (!optionalSpan) {
                        optionalSpan = document.createElement("span");
                        optionalSpan.className = "smart-sizing-tool__label-optional";
                        optionalSpan.textContent = "(Optional)";
                        modelLabel.appendChild(optionalSpan);
                    } else {
                        optionalSpan.style.display = "";
                    }
                } else {
                    // Show required indicator, hide optional
                    if (requiredSpan) requiredSpan.style.display = "";
                    if (optionalSpan) optionalSpan.style.display = "none";
                }
            }
        }
    }

    /**
     * Enable/disable the model selector based on brand requirement and current brand selection.
     * When brand is required and not selected, the model selector is visually and functionally disabled.
     */
    updateModelEnabledState() {
        if (!this.ruleSet) return;

        const brandRequirement = this.ruleSet.brand_requirement;
        const url = new URL(window.location.href);
        const brand = url.searchParams.get("brand");

        const modelSelectorWrapper = this.querySelector(".smart-sizing-tool__model-selector");
        const modelSelector = this.querySelector('[data-component-id="model-selector"]');
        if (!modelSelectorWrapper || !modelSelector) return;

        const shouldDisable = brandRequirement === "required" && (!brand || !brand.trim());

        if (shouldDisable) {
            modelSelectorWrapper.classList.add("is-disabled");
            modelSelectorWrapper.style.pointerEvents = "none";
            modelSelectorWrapper.style.opacity = "0.5";
            modelSelector.setAttribute("aria-disabled", "true");
        } else {
            modelSelectorWrapper.classList.remove("is-disabled");
            modelSelectorWrapper.style.pointerEvents = "";
            modelSelectorWrapper.style.opacity = "";
            modelSelector.removeAttribute("aria-disabled");
        }
    }

    renderBrands() {
        const _ruleSet = this.ruleSet;

        // Get all unique brands
        let brands = [
            ...new Set(
                _ruleSet.combos.map((combo) => combo.models.map((model) => model.brand)).flat(),
            ),
        ];

        // Filter brands based on brand_scope: hide Starmark when scope is Non Starmark Only
        const brandScope = _ruleSet.brand_scope;
        if (brandScope === "Non Starmark Only") {
            brands = brands.filter((b) => String(b).toLowerCase() !== "starmark");
        } else if (brandScope === "Starmark Only") {
            brands = brands.filter((b) => String(b).toLowerCase() === "starmark");
        }

        // Flatten all models from combos once
        const allModels = _ruleSet.combos
            .map((combo) =>
                combo.models.map((model) => {
                    return {
                        brand: model.brand,
                        model: model.model_name,
                    };
                }),
            )
            .flat();

        // Build brand object with unique models per brand (only for filtered brands)
        _ruleSet._brand = brands.reduce((acc, brand) => {
            const brandModels = allModels.filter((m) => m.brand === brand);
            const uniqueModels = [...new Set(brandModels.map((m) => m.model))];

            acc[brand] = {
                models: uniqueModels,
                models_count: uniqueModels.length,
            };

            return acc;
        }, {});

        // Keep brands as array for backward compatibility
        _ruleSet.brands = brands;

        // Render brand options
        const brandSelector = this.querySelector('[data-component-id="brand-selector"]');
        const brandOptionsContainer = brandSelector.querySelector(".dropdown-component__options");
        const brandTemplate = this.querySelector("#brand-option-template");

        if (!brandTemplate) {
            console.error("Brand template not found");
            return;
        }

        // Clear previous options but keep the template
        const existingOptions = brandOptionsContainer.querySelectorAll(
            ".dropdown-component__option",
        );
        existingOptions.forEach((option) => option.remove());

        Object.keys(_ruleSet._brand).forEach((brand) => {
            const brandOptionTemplate = brandTemplate.content.cloneNode(true);

            brandOptionTemplate.querySelector(".dropdown-component__option-text").textContent =
                brand;

            brandOptionTemplate
                .querySelector(".dropdown-component__option")
                .setAttribute("data-brand", brand);
            brandOptionsContainer.appendChild(brandOptionTemplate);
        });

        // Add "Other" option
        // const otherOptionTemplate = brandTemplate.content.cloneNode(true);
        // otherOptionTemplate.querySelector(".dropdown-component__option-text").textContent = "Other";
        // otherOptionTemplate
        //     .querySelector(".dropdown-component__option")
        //     .setAttribute("data-brand", "Other");
        // brandOptionsContainer.appendChild(otherOptionTemplate);

        // Create custom brand input (initially hidden)
        this.createCustomBrandInput();

        // Listen for brand selection
        brandOptionsContainer.addEventListener("click", (e) => {
            const option = e.target.closest(".dropdown-component__option");
            if (option) {
                const selectedBrand = option.getAttribute("data-brand");
                if (selectedBrand === "Other") {
                    // Show custom brand input
                    const customBrandInput = this.querySelector(
                        ".smart-sizing-tool__custom-brand-input",
                    );
                    if (customBrandInput) {
                        customBrandInput.classList.remove("hidden");
                        customBrandInput.querySelector("input").focus();
                    }

                    // For brand "Other", only render 'Other' option in model selector
                    this.renderModels("Other");

                    // Update model visibility (will handle hidden state)
                    this.updateModelVisibility();

                    // Keep custom model input hidden until model "Other" is chosen
                    const customModelInput = this.querySelector(
                        ".smart-sizing-tool__custom-model-input",
                    );
                    if (customModelInput) {
                        customModelInput.classList.add("hidden");
                        customModelInput.querySelector("input").value = "";
                    }

                    // Update URL
                    const url = new URL(window.location.href);
                    url.searchParams.set("brand", "Other");
                    // Reset all selection-related params when switching to "Other" brand
                    [
                        "model",
                        "custom_model",
                        "inside_widths",
                        "adjustable_beds",
                        "couch_types",
                        "fabrics",
                    ].forEach((key) => {
                        url.searchParams.delete(key);
                    });
                    window.history.replaceState({}, "", url.toString());

                    // Update model enabled/disabled state AFTER URL is updated
                    this.updateModelEnabledState();

                    // Reset model selector UI to initial state
                    this.resetHeadingModels();

                    // Reset UI: remove active class from all option buttons
                    this.resetOptionButtons();

                    // Clear field errors when brand is selected
                    this.clearFieldErrors();

                    // Hide group options and suggested products when "Other" is selected
                    this.updateGroupOptionsVisibility();
                    this.updateOptionAvailability();
                    this.updateSuggestProductsVisibility();
                } else if (selectedBrand && _ruleSet._brand[selectedBrand]) {
                    // Remove custom brand param when regular brand is selected
                    this.removeParamsFromUrl(["custom_brand"]);
                    // Remove custom model param when switching brands
                    this.removeParamsFromUrl(["custom_model"]);

                    // Hide custom brand input when regular brand is selected
                    const customBrandInput = this.querySelector(
                        ".smart-sizing-tool__custom-brand-input",
                    );
                    if (customBrandInput) {
                        customBrandInput.classList.add("hidden");
                        customBrandInput.querySelector("input").value = "";
                    }

                    // Hide custom model input when switching brands
                    const customModelInput = this.querySelector(
                        ".smart-sizing-tool__custom-model-input",
                    );
                    if (customModelInput) {
                        customModelInput.classList.add("hidden");
                        customModelInput.querySelector("input").value = "";
                    }

                    this.renderModels(selectedBrand);

                    // Update model visibility (will handle hidden state)
                    this.updateModelVisibility();

                    // Sync brand selection to URL
                    const url = new URL(window.location.href);
                    url.searchParams.set("brand", selectedBrand);
                    // Remove model param when brand changes (new brand, no model selected yet)
                    url.searchParams.delete("model");
                    // Reset all option params when brand changes
                    ["inside_widths", "adjustable_beds", "couch_types", "fabrics"].forEach(
                        (key) => {
                            url.searchParams.delete(key);
                        },
                    );
                    window.history.replaceState({}, "", url.toString());

                    // Update model enabled/disabled state AFTER URL is updated
                    this.updateModelEnabledState();

                    // Reset model selector UI to initial state
                    this.resetHeadingModels();

                    // Reset UI: remove active class from all option buttons
                    this.resetOptionButtons();

                    // Clear field errors when brand is selected
                    this.clearFieldErrors();

                    // Hide group options until model is selected
                    this.updateGroupOptionsVisibility();
                    this.updateOptionAvailability();
                    this.updateSuggestProductsVisibility();
                }
            }
        });
    }

    removeParamsFromUrl(params) {
        const url = new URL(window.location.href);
        params.forEach((param) => {
            url.searchParams.delete(param);
        });
        window.history.replaceState({}, "", url.toString());
    }

    resetOptionButtons() {
        // Reset all active buttons in option groups
        const groupKeys = ["inside_widths", "adjustable_beds", "couch_types", "fabrics"];
        groupKeys.forEach((key) => {
            const groupItem = this.querySelector(
                `.smart-sizing-tool__select-group-item[data-key="${key.toLowerCase()}"]`,
            );
            if (groupItem) {
                const buttons = groupItem.querySelectorAll(
                    ".smart-sizing-tool__select-group-item-button.active",
                );
                buttons.forEach((btn) => btn.classList.remove("active"));
            }
        });
    }

    createCustomBrandInput() {
        // Check if input already exists
        if (this.querySelector(".smart-sizing-tool__custom-brand-input")) {
            return;
        }

        const brandSelector = this.querySelector('[data-component-id="brand-selector"]');
        if (!brandSelector) return;

        // create label
        const label = document.createElement("label");
        label.textContent = "Brand (Other)";
        label.className = "smart-sizing-tool__custom-brand-input-label";
        label.style.marginBottom = "10px";
        label.style.fontSize = "14px";
        label.style.fontWeight = "bold";
        label.style.color = "#231f20";

        // insert label before input
        // Create input container
        const inputContainer = document.createElement("div");
        inputContainer.className = "hidden smart-sizing-tool__custom-brand-input";
        inputContainer.style.marginBottom = "18px";

        // Create input
        const input = document.createElement("input");
        input.type = "text";
        input.placeholder = "Enter brand name";
        input.className = "smart-sizing-tool__custom-brand-input-field";
        input.style.width = "100%";
        input.style.padding = "10px";
        input.style.borderRadius = "3px";
        input.style.border = "1px solid #231f20";

        // Add input event listener to sync to URL
        input.addEventListener("input", (e) => {
            const customBrand = e.target.value.trim();
            const url = new URL(window.location.href);
            if (customBrand) {
                url.searchParams.set("custom_brand", customBrand);
            } else {
                url.searchParams.delete("custom_brand");
            }
            window.history.replaceState({}, "", url.toString());
        });

        inputContainer.appendChild(label);
        inputContainer.appendChild(input);

        // Insert after brand selector parent div
        const brandSelectorParent = brandSelector.parentElement;
        if (brandSelectorParent) {
            brandSelectorParent.insertAdjacentElement("afterend", inputContainer);
        }
    }

    createCustomModelInput() {
        // Check if input already exists
        if (this.querySelector(".smart-sizing-tool__custom-model-input")) {
            return;
        }

        const modelSelectorWrapper = this.querySelector(".smart-sizing-tool__model-selector");
        if (!modelSelectorWrapper) return;

        // create label
        const label = document.createElement("label");
        label.textContent = "Ceremonial Casket Model (Other)";
        label.className = "smart-sizing-tool__custom-model-input-label";
        label.style.marginBottom = "10px";
        label.style.fontSize = "14px";
        label.style.fontWeight = "bold";
        label.style.color = "#231f20";

        // Create input container
        const inputContainer = document.createElement("div");
        inputContainer.className = "hidden smart-sizing-tool__custom-model-input";
        inputContainer.style.marginTop = "18px";

        // Create input
        const input = document.createElement("input");
        input.type = "text";
        input.placeholder = "Enter casket model";
        input.className = "smart-sizing-tool__custom-model-input-field";
        input.style.width = "100%";
        input.style.padding = "10px";
        input.style.borderRadius = "3px";
        input.style.border = "1px solid #231f20";

        // Add input event listener to sync to URL and clear errors
        input.addEventListener("input", (e) => {
            const customModel = e.target.value.trim();
            const url = new URL(window.location.href);
            if (customModel) {
                url.searchParams.set("custom_model", customModel);
                // Clear error when user starts typing
                input.classList.remove("has-error");
                const errorElement = inputContainer.querySelector(
                    ".smart-sizing-tool__field-error",
                );
                if (errorElement) {
                    errorElement.style.display = "none";
                }
            } else {
                url.searchParams.delete("custom_model");
            }
            window.history.replaceState({}, "", url.toString());
        });

        inputContainer.appendChild(label);
        inputContainer.appendChild(input);

        // Insert after model selector wrapper
        modelSelectorWrapper.insertAdjacentElement("afterend", inputContainer);
    }

    resetHeadingModels() {
        const heading = this.querySelector(".dropdown-component__heading[data-heading='models']");
        if (heading) {
            // Update only the text node, keep the icon
            const headingTextNode = heading.childNodes[0];
            if (headingTextNode && headingTextNode.nodeType === Node.TEXT_NODE) {
                headingTextNode.nodeValue = "Select a model ";
            }
            heading.style.color = "#9d9da4";
        }
    }

    renderModels(selectedBrand = null) {
        const _ruleSet = this.ruleSet;
        const modelSelector = this.querySelector('[data-component-id="model-selector"]');

        // If model selector doesn't exist (e.g., hidden), skip rendering
        if (!modelSelector) {
            return;
        }

        const modelOptionsContainer = modelSelector.querySelector(".dropdown-component__options");
        const modelTemplate = this.querySelector("#model-option-template");

        if (!modelTemplate) {
            console.error("Model template not found");
            return;
        }

        if (!modelOptionsContainer) {
            console.error("Model options container not found");
            return;
        }

        // Clear previous options but keep the template
        const existingOptions = modelOptionsContainer.querySelectorAll(
            ".dropdown-component__option",
        );
        existingOptions.forEach((option) => option.remove());

        // Check if brand is hidden - if so, show all models regardless of selectedBrand
        const brandVisibility = this.ruleSet.brand_visibility;
        const isBrandHidden = brandVisibility === "hide";

        // If brand is selected and brand is not hidden, filter models by brand
        let modelsToRender = [];
        if (!isBrandHidden && selectedBrand && _ruleSet._brand[selectedBrand]) {
            modelsToRender = _ruleSet._brand[selectedBrand].models;
        } else {
            // If brand is hidden or no brand selected, show unique models (optionally filtered by brand_scope)
            const allModelsFlat = _ruleSet.combos.flatMap((combo) =>
                combo.models
                .map((model) => ({
                    brand: String(model.brand || "").trim(),
                    model_name: String(model.model_name || "").trim(),
                }))
                .filter((m) => m.brand && m.model_name),
            );
            // When brand is hidden, filter models by brand_scope (Starmark Only / Non Starmark Only / All)
            const brandScope = _ruleSet.brand_scope;
            if (brandScope === "Starmark Only") {
                modelsToRender = [
                    ...new Set(
                        allModelsFlat
                        .filter((m) => m.brand && String(m.brand).toLowerCase() === "starmark")
                        .map((m) => m.model_name),
                    ),
                ];
            } else if (brandScope === "Non Starmark Only") {
                modelsToRender = [
                    ...new Set(
                        allModelsFlat
                        .filter((m) => !m.brand || String(m.brand).toLowerCase() !== "starmark")
                        .map((m) => m.model_name),
                    ),
                ];
            } else {
                // "All" or any Other value: show all models
                modelsToRender = [...new Set(allModelsFlat.map((m) => m.model_name))];
            }
        }

        // Always keep "Other" (if present) as the last option in the dropdown
        const otherIndex = modelsToRender.indexOf("Other");
        if (otherIndex !== -1 && otherIndex !== modelsToRender.length - 1) {
            modelsToRender.splice(otherIndex, 1);
            modelsToRender.push("Other");
        }

        // Store models for backward compatibility
        _ruleSet.models = modelsToRender;

        modelsToRender.forEach((model) => {
            const modelOptionTemplate = modelTemplate.content.cloneNode(true);
            const optionElement = modelOptionTemplate.querySelector(".dropdown-component__option");

            optionElement.setAttribute("data-model", model);
            modelOptionTemplate.querySelector(".dropdown-component__option-text").textContent =
                model;

            modelOptionsContainer.appendChild(modelOptionTemplate);
        });
        // Create custom model input (initially hidden)
        this.createCustomModelInput();
    }

    initAddToCartListener() {
        // Use event delegation so dynamically-loaded sections (get-metaobject) work too
        this.addEventListener("click", async (e) => {
            const btn = e.target.closest(".smart-sizing-tool__add-to-cart-btn");
            if (!btn) return;

            e.preventDefault();
            const variantId = btn.dataset.variantId;

            if (!variantId) {
                console.error("Variant ID not found");
                return;
            }

            // Disable button during request
            btn.disabled = true;
            btn.classList.add("is-loading");

            try {
                // Build properties using shared helper
                const properties = this.getLineItemProperties();

                // Build form data
                const formData = new FormData();
                formData.append("id", variantId);

                // Respect quantity_rule increment when provided in markup
                const quantityIncAttr =
                    btn.dataset.quantityInc ||
                    (this.suggestProducts ?
                        this.suggestProducts.getAttribute("data-quantity-inc") :
                        null);
                const quantityInc = parseInt(quantityIncAttr, 10);
                const quantity = Number.isFinite(quantityInc) && quantityInc > 0 ? quantityInc : 1;
                formData.append("quantity", String(quantity));

                // Add properties
                Object.entries(properties).forEach(([key, value]) => {
                    formData.append(`properties[${key}]`, value);
                });

                // Add sections for cart update
                formData.append("sections_url", window.location.pathname);
                formData.append("sections", "cart-icon-bubble");

                // Fetch cart drawer section if exists
                const cartDrawer = document.querySelector("cart-drawer");
                if (cartDrawer) {
                    const cartDrawerSection = cartDrawer.closest(".shopify-section");
                    if (cartDrawerSection) {
                        formData.append(
                            "sections",
                            `,${cartDrawerSection.id.replace("shopify-section-", "")}`,
                        );
                    }
                }

                // Add to cart
                const response = await fetch(theme.routes.cartAdd, {
                    method: "POST",
                    headers: {
                        Accept: "application/javascript",
                        "X-Requested-With": "XMLHttpRequest",
                    },
                    body: formData,
                });

                const data = await response.json();

                if (!response.ok || data.status) {
                    throw new Error(data.description || data.message || "Failed to add to cart");
                }

                // Update cart icon
                if (window.ProductForm && typeof window.ProductForm.updateCartIcon === "function") {
                    window.ProductForm.updateCartIcon(data);
                }

                // Show success state
                btn.classList.remove("is-loading");
                btn.classList.add("is-success");
                btn.textContent = "Added to cart";

                // Update cart drawer if exists
                if (cartDrawer) {
                    setTimeout(() => {
                        cartDrawer.renderContents(data, false);
                    }, 700);
                }

                // Reset button after delay
                setTimeout(() => {
                    btn.classList.remove("is-success");
                    btn.textContent =
                        theme.strings && theme.strings.addToCart ?
                        theme.strings.addToCart :
                        "Add to cart";
                    btn.disabled = false;
                }, 2000);
            } catch (error) {
                console.error("Error adding to cart:", error);
                btn.classList.remove("is-loading");
                btn.disabled = false;
                // Show error message
                alert(error.message || "Failed to add product to cart");
            }
        });
    }

    /**
     * Public helper to build line item properties from current URL + ruleSet.
     * Can be used by external add-to-cart flows (e.g. main product form).
     * Returns an object of properties; if empty, caller can skip adding them.
     *
     * Logic:
     * - Hide: rental_casket_insert_rule_set, is_generic
     * - When brand/model is "Other": only include *_other property, not the main property
     * - For couch_types and fabrics: use human-readable values from matching combo, not URL handles
     */
    getLineItemProperties() {
        const properties = {};

        if (!this.ruleSet) return properties;

        // Get all URL params to build properties
        const url = new URL(window.location.href);
        const params = url.searchParams;

        // Find matching combo to get human-readable values for couch_type and fabric
        const matchingCombo = this.findMatchingCombo();

        // Brand
        const brand = params.get("brand");
        if (brand) {
            if (brand === "Other") {
                // When brand is "Other", only include custom brand property with readable label
                const customBrand = params.get("custom_brand");
                if (customBrand) {
                    properties["Brand Name (if Other)"] = customBrand;
                }
            } else {
                properties["Brand"] = brand;
            }
        }

        // Model
        const model = params.get("model");
        if (model) {
            if (model === "Other") {
                // When model is "Other", only include custom model property with readable label
                const customModel = params.get("custom_model");
                if (customModel) {
                    properties["Ceremonial Casket Model (if Other)"] = customModel;
                }
            } else {
                properties["Ceremonial Casket Model"] = model;
            }
        }

        // Option groups
        const insideWidth = params.get("inside_widths");
        if (insideWidth) {
            properties["Inside Width"] = insideWidth;
        }

        const hideAdjustableBed =
            this.ruleSet.hide_adjustable_bed_if_one_value === true ||
            this.ruleSet.hide_adjustable_bed_if_one_value === "true";
        if (!hideAdjustableBed) {
            const adjustableBed = params.get("adjustable_beds");
            if (adjustableBed !== null && adjustableBed !== "") {
                const bedValue = adjustableBed === "true" || adjustableBed === true ? "Yes" : "No";
                properties["Adjustable Bed"] = bedValue;
            }
        }

        // Couch type: use human-readable value from matching combo if available
        const couchTypeHandle = params.get("couch_types");
        if (couchTypeHandle) {
            let couchTypeValue = null;
            if (matchingCombo && matchingCombo.couch_type) {
                // Use original human-readable value from combo
                couchTypeValue = matchingCombo.couch_type;
            } else {
                // Fallback: try to find original value from combos
                const combo = this.ruleSet.combos.find(
                    (c) => c.active && this.sanitizeForHandle(c.couch_type) === couchTypeHandle,
                );
                if (combo && combo.couch_type) {
                    couchTypeValue = combo.couch_type;
                } else {
                    // Last resort: use handle value
                    couchTypeValue = couchTypeHandle;
                }
            }
            if (couchTypeValue) {
                properties["Couch Type"] = couchTypeValue;
            }
        }

        // Fabric: use human-readable value from matching combo if available
        const fabricHandle = params.get("fabrics");
        if (fabricHandle) {
            let fabricValue = null;
            if (matchingCombo && matchingCombo.fabric) {
                // Use original human-readable value from combo
                fabricValue = matchingCombo.fabric;
            } else {
                // Fallback: try to find original value from combos
                const combo = this.ruleSet.combos.find(
                    (c) => c.active && this.sanitizeForHandle(c.fabric) === fabricHandle,
                );
                if (combo && combo.fabric) {
                    fabricValue = combo.fabric;
                } else {
                    // Last resort: use handle value
                    fabricValue = fabricHandle;
                }
            }
            if (fabricValue) {
                properties["Fabric"] = fabricValue;
            }
        }

        // Note: rental_casket_insert_rule_set and is_generic are intentionally excluded
        // as they should not be displayed in cart line item properties

        return properties;
    }

    /**
     * Shows error messages at specific fields
     * @param {string[]} missingFields - Array of field keys that are missing
     */
    showFieldErrors(missingFields) {
        // Clear all existing errors first
        this.clearFieldErrors();

        missingFields.forEach((fieldKey) => {
            let errorElement = null;
            let targetElement = null;

            if (fieldKey === "brand") {
                const url = new URL(window.location.href);
                const params = url.searchParams;
                const brand = params.get("brand");

                // If brand is "Other" but custom_brand is empty, show error at custom input
                if (brand === "Other") {
                    // Ensure custom brand input exists and is visible
                    if (!this.querySelector(".smart-sizing-tool__custom-brand-input")) {
                        this.createCustomBrandInput();
                    }

                    const customBrandInput = this.querySelector(
                        ".smart-sizing-tool__custom-brand-input",
                    );
                    if (customBrandInput) {
                        // Ensure custom input is visible
                        customBrandInput.classList.remove("hidden");
                        customBrandInput.style.display = "";

                        const input = customBrandInput.querySelector("input");
                        if (input) {
                            input.classList.add("has-error");
                            errorElement = customBrandInput.querySelector(
                                ".smart-sizing-tool__field-error",
                            );
                            if (!errorElement) {
                                errorElement = document.createElement("div");
                                errorElement.className = "smart-sizing-tool__field-error";
                                errorElement.textContent = "Please enter a brand name";
                                errorElement.style.marginTop = "6px";
                                errorElement.style.display = "block";
                                errorElement.style.color = "#ff0000";
                                errorElement.style.fontSize = "14px";
                                // Append after input (inputContainer contains both label and input)
                                customBrandInput.appendChild(errorElement);
                            }
                            errorElement.style.display = "block";
                            console.log("[Smart Sizing Tool] Brand error element:", errorElement);

                            // Scroll to error
                            if (missingFields.indexOf(fieldKey) === 0) {
                                setTimeout(() => {
                                    customBrandInput.scrollIntoView({
                                        behavior: "smooth",
                                        block: "center",
                                    });
                                }, 100);
                            }
                        } else {
                            console.warn(
                                "[Smart Sizing Tool] Brand input not found in customBrandInput",
                            );
                        }
                    } else {
                        console.warn("[Smart Sizing Tool] Custom brand input container not found");
                    }
                } else {
                    // Regular brand selection error
                    const brandSelector = this.querySelector(
                        '[data-component-id="brand-selector"]',
                    );
                    if (brandSelector) {
                        targetElement = brandSelector.closest("div");
                        errorElement = targetElement.querySelector(
                            ".smart-sizing-tool__field-error",
                        );
                        if (!errorElement) {
                            errorElement = document.createElement("div");
                            errorElement.className = "smart-sizing-tool__field-error";
                            errorElement.textContent = "Please select a brand";
                            targetElement.appendChild(errorElement);
                        }
                        errorElement.style.display = "";
                        brandSelector.classList.add("has-error");
                    }
                }
            } else if (fieldKey === "model") {
                const url = new URL(window.location.href);
                const params = url.searchParams;
                const model = params.get("model");

                // If model is "Other" but custom_model is empty, show error at custom input
                if (model === "Other") {
                    // Ensure custom model input exists and is visible
                    if (!this.querySelector(".smart-sizing-tool__custom-model-input")) {
                        this.createCustomModelInput();
                    }

                    const customModelInput = this.querySelector(
                        ".smart-sizing-tool__custom-model-input",
                    );
                    if (customModelInput) {
                        // Ensure custom input is visible
                        customModelInput.classList.remove("hidden");
                        customModelInput.style.display = "";

                        const input = customModelInput.querySelector("input");
                        if (input) {
                            input.classList.add("has-error");
                            errorElement = customModelInput.querySelector(
                                ".smart-sizing-tool__field-error",
                            );
                            if (!errorElement) {
                                errorElement = document.createElement("div");
                                errorElement.className = "smart-sizing-tool__field-error";
                                errorElement.textContent = "Please enter a model name";
                                errorElement.style.marginTop = "6px";
                                errorElement.style.display = "block";
                                errorElement.style.color = "#ff0000";
                                errorElement.style.fontSize = "14px";
                                // Append after input (inputContainer contains both label and input)
                                customModelInput.appendChild(errorElement);
                            }
                            errorElement.style.display = "block";
                            console.log("[Smart Sizing Tool] Model error element:", errorElement);

                            // Scroll to error
                            if (missingFields.indexOf(fieldKey) === 0) {
                                setTimeout(() => {
                                    customModelInput.scrollIntoView({
                                        behavior: "smooth",
                                        block: "center",
                                    });
                                }, 100);
                            }
                        } else {
                            console.warn(
                                "[Smart Sizing Tool] Model input not found in customModelInput",
                            );
                        }
                    } else {
                        console.warn("[Smart Sizing Tool] Custom model input container not found");
                    }
                } else {
                    // Regular model selection error
                    const modelSelectorWrapper = this.querySelector(
                        ".smart-sizing-tool__model-selector",
                    );
                    const modelSelector = this.querySelector(
                        '[data-component-id="model-selector"]',
                    );
                    // Only show error if model selector is visible (not hidden)
                    if (
                        modelSelector &&
                        modelSelectorWrapper &&
                        modelSelectorWrapper.style.display !== "none" &&
                        !modelSelectorWrapper.classList.contains("hidden")
                    ) {
                        targetElement = modelSelector.closest("div");
                        if (!targetElement) {
                            targetElement = modelSelectorWrapper;
                        }
                        errorElement = targetElement.querySelector(
                            ".smart-sizing-tool__field-error",
                        );
                        if (!errorElement) {
                            errorElement = document.createElement("div");
                            errorElement.className = "smart-sizing-tool__field-error";
                            errorElement.textContent = "Please select a model";
                            targetElement.appendChild(errorElement);
                        }
                        errorElement.style.display = "";
                        modelSelector.classList.add("has-error");
                    }
                }
            } else {
                // Option groups (inside_widths, adjustable_beds, couch_types, fabrics)
                const groupItem = this.querySelector(
                    `.smart-sizing-tool__select-group-item[data-key="${fieldKey.toLowerCase()}"]`,
                );
                if (groupItem) {
                    const label = groupItem.querySelector(
                        ".smart-sizing-tool__select-group-item-label",
                    );
                    if (label) {
                        errorElement = label.querySelector(".smart-sizing-tool__field-error");
                        if (!errorElement) {
                            errorElement = document.createElement("div");
                            errorElement.className = "smart-sizing-tool__field-error";
                            errorElement.textContent = "Please select an option";
                            label.appendChild(errorElement);
                        }
                        errorElement.style.display = "";
                        groupItem.classList.add("has-error");
                    }
                }
            }

            // Scroll to first error field
            if (errorElement && missingFields.indexOf(fieldKey) === 0) {
                setTimeout(() => {
                    errorElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }, 100);
            }
        });
    }

    /**
     * Clears all field error messages
     */
    clearFieldErrors() {
        // Remove error messages
        const errorElements = this.querySelectorAll(".smart-sizing-tool__field-error");
        errorElements.forEach((el) => {
            el.style.display = "none";
        });

        // Remove error classes from dropdowns
        const brandSelector = this.querySelector('[data-component-id="brand-selector"]');
        if (brandSelector) brandSelector.classList.remove("has-error");

        const modelSelector = this.querySelector('[data-component-id="model-selector"]');
        if (modelSelector) modelSelector.classList.remove("has-error");

        // Remove error classes from custom inputs
        const customBrandInput = this.querySelector(".smart-sizing-tool__custom-brand-input");
        if (customBrandInput) {
            const input = customBrandInput.querySelector("input");
            if (input) input.classList.remove("has-error");
        }

        const customModelInput = this.querySelector(".smart-sizing-tool__custom-model-input");
        if (customModelInput) {
            const input = customModelInput.querySelector("input");
            if (input) input.classList.remove("has-error");
        }

        // Remove error classes from option groups
        const groupItems = this.querySelectorAll(".smart-sizing-tool__select-group-item");
        groupItems.forEach((item) => item.classList.remove("has-error"));
    }

    /**
     * Validates that all required fields are filled before Add to Cart.
     * Returns an object with validation result and missing fields.
     * @returns {{isValid: boolean, missingFields: string[], errorMessage: string}}
     */
    validateRequiredFields() {
        if (!this.ruleSet) {
            return {
                isValid: true,
                missingFields: [],
                errorMessage: "",
            };
        }

        const url = new URL(window.location.href);
        const params = url.searchParams;
        const missingFields = [];
        const fieldLabels = {
            brand: "Brand",
            model: "Ceremonial Casket Model",
            inside_widths: "Inside Width",
            adjustable_beds: "Adjustable Bed",
            couch_types: "Couch Type",
            fabrics: "Fabric",
        };

        // Check brand requirement
        const brandRequirement = this.ruleSet.brand_requirement;
        if (brandRequirement === "required") {
            const brand = params.get("brand");
            if (!brand || brand.trim() === "") {
                missingFields.push("brand");
            } else if (brand === "Other") {
                // If brand is "Other", custom_brand must be filled
                const customBrand = params.get("custom_brand");
                if (!customBrand || customBrand.trim() === "") {
                    missingFields.push("brand");
                }
            }
        }

        // Check model requirement (only validate if required and not hidden)
        const modelRequirement = this.ruleSet.model_requirement;
        if (modelRequirement === "required") {
            const model = params.get("model");
            if (!model || model.trim() === "") {
                missingFields.push("model");
            } else if (model === "Other") {
                // If model is "Other", custom_model must be filled
                const customModel = params.get("custom_model");
                if (!customModel || customModel.trim() === "") {
                    missingFields.push("model");
                }
            }
        }
        // Note: If modelRequirement is "hidden" or "optional", model is not validated

        // Note: Option groups (inside_widths, adjustable_beds, couch_types, fabrics) are optional
        // and should not be validated as required fields. They are only used for matching combos
        // but are not mandatory for adding to cart.

        const isValid = missingFields.length === 0;
        let errorMessage = "";
        if (!isValid) {
            const missingLabels = missingFields.map((field) => fieldLabels[field] || field);
            errorMessage = `Please select: ${missingLabels.join(", ")}`;
        }

        return {
            isValid,
            missingFields,
            errorMessage,
        };
    }

    /**
     * Public helper to expose the currently suggested product variant ID (if any)
     * so that the main product form can add it to cart together with the main product.
     * When brand and model are both "Other", returns generic product variant ID so it gets added to cart.
     */
    getSuggestedVariantId() {
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const brand = params.get("brand");
        const model = params.get("model");

        const isOtherBoth = brand === "Other" && model === "Other";

        // When customer selects "Other" for both brand and model, add generic product to cart
        if (
            isOtherBoth &&
            this.ruleSet &&
            this.ruleSet.generic_insert_product &&
            this.ruleSet.generic_insert_product.variant_id
        ) {
            const genericVariantId = String(this.ruleSet.generic_insert_product.variant_id).trim();
            if (genericVariantId) return genericVariantId;
        }

        if (!this.suggestProducts) {
            console.warn("[Smart Sizing Tool] suggestProducts element not found");
            return null;
        }

        // Check if element is visible (not hidden)
        if (
            this.suggestProducts.style.display === "none" ||
            this.suggestProducts.classList.contains("hidden")
        ) {
            return null;
        }

        // Try both dataset and getAttribute for compatibility
        const variantId =
            this.suggestProducts.dataset.variantId ||
            this.suggestProducts.getAttribute("data-variant-id");

        // Ensure variant ID is not empty
        if (!variantId || variantId.trim() === "") {
            console.warn(
                "[Smart Sizing Tool] No valid variant-id found on suggestProducts element",
                this.suggestProducts,
                "Attributes:",
                Array.from(this.suggestProducts.attributes).map(
                    (attr) => `${attr.name}="${attr.value}"`,
                ),
            );
            return null;
        }

        return variantId.trim();
    }

    getQuantityInc() {
        if (!this.suggestProducts) {
            console.warn("[Smart Sizing Tool] suggestProducts element not found");
            return null;
        }

        return (
            this.suggestProducts.dataset.quantityInc ||
            this.suggestProducts.getAttribute("data-quantity-inc")
        );
    }
}

customElements.define("smart-sizing-tool", SmartSizingTool);